import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { ReducerEntityPrefixTypes, ReducerNamespaceTypes } from "enums";
import { type Scorm } from "../../services/types";
import { type Settings, type FormFields } from "../../types/models";
import type { FileLike } from "../../../../../interfaces";

export interface DetailsState {
  item: {
    id: number;
    title: string;
    description: string;
    thumbnailUrl: string;
    isDraft: boolean;
    isPurchased: boolean;
    hasBeenPublished: boolean;
    dateModified?: string;
    publisherId: number;
    settings: Settings;
    fileName: string;
    canBePublished: boolean;
    packagePath: string;
  };
  uploadedFiles: FileLike[];
  isLoading: boolean;
  isLoaded: boolean;
  isUploaded: boolean;
  isUploading: boolean;
}

const initialState: DetailsState = {
  item: {
    id: -1,
    title: "",
    description: "",
    thumbnailUrl: "",
    isDraft: true,
    isPurchased: false,
    hasBeenPublished: false,
    canBePublished: false,
    dateModified: undefined,
    publisherId: -1,
    fileName: "",
    packagePath: "",
    settings: {
      labels: [],
      softwareApplications: [],
      flows: [],
      packs: [],
    },
  },
  uploadedFiles: [],
  isLoaded: false,
  isLoading: false,
  isUploaded: false,
  isUploading: false,
};

const detailsSlice = createSlice({
  name: `${ReducerNamespaceTypes.Library}/${ReducerEntityPrefixTypes.SCORM}/details`,
  initialState,
  reducers: {
    reset: () => initialState,
    save(state: DetailsState, action: PayloadAction<FormFields>) {
      state.item.title = action.payload.title;
      state.item.description = action.payload.description;
      state.item.fileName = action.payload.title;
      state.item.thumbnailUrl = action.payload.thumbnailUrl;
      state.uploadedFiles = action.payload.uploadedFiles;
    },
    saveSettings(state: DetailsState, action: PayloadAction<{ labels: string[]; softwareApplications: string[] }>) {
      state.item.settings.labels = action.payload.labels;
      state.item.settings.softwareApplications = action.payload.softwareApplications;
    },
    fetchBegin(state: DetailsState) {
      state.isLoading = true;
    },
    fetchSuccess(state: DetailsState, action: PayloadAction<Scorm>) {
      const { packs, flows, labels, softwareApplications, ...rest } = action.payload;

      state.isLoaded = true;
      state.isLoading = false;
      state.item = {
        ...rest,
        settings: {
          labels,
          softwareApplications,
          flows,
          packs: packs.map((pack) => ({ title: pack.name, id: pack.id })),
        },
      };
    },
    fetchFailure(state: DetailsState) {
      state.isLoading = false;
    },
    setLoaded(state: DetailsState) {
      state.isLoaded = true;
    },
    uploadBegin(state: DetailsState) {
      state.isUploading = true;
    },
    uploadSuccess(state: DetailsState) {
      state.isUploaded = true;
      state.isUploading = false;
      state.item.canBePublished = false;
    },
    uploadCancel(state: DetailsState) {
      state.isUploaded = false;
      state.isUploading = false;
      state.uploadedFiles = [];
    },
    uploadProcessingSuccess(state: DetailsState) {
      state.item.canBePublished = true;
    },
    saveThumbnail(state: DetailsState, action: PayloadAction<string>) {
      state.item.thumbnailUrl = action.payload;
    },
  },
});

export const {
  save,
  saveSettings,
  fetchBegin,
  fetchSuccess,
  fetchFailure,
  reset,
  setLoaded,
  uploadBegin,
  uploadSuccess,
  uploadCancel,
  uploadProcessingSuccess,
  saveThumbnail,
} = detailsSlice.actions;

export default detailsSlice.reducer;
